import {
  AlertOctagon,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Search,
} from 'lucide-react';
import { Fragment, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next'; // <-- NEU
import type { GradingScaleBorder } from '../../../../../../shared/grading-scales.js';
import { PREDEFINED_GRADING_SCALES } from '../../../../../../shared/grading-scales.js';
import type { GrammarFiThreshold } from '../../../../../../shared/grammar-grading.js';
import { GRADING_THRESHOLDS } from '../../../../../lib/constants.js';
import { cn } from '../../../../../lib/utils.js';
import type { SummaryStateStore } from '../hooks/use-summary-state.js';
import type { StudentSummary } from '../utils.js';
import { roundToOne } from '../utils.js';
import { DistributeResultsCard } from './distribute-results-card.js';

export function KpiCard({
  label,
  value,
  detail,
  compact = false,
  className,
}: {
  label: string;
  value: string;
  detail?: string;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-card shadow-sm',
        compact ? 'p-2' : 'p-3',
        className,
      )}
    >
      <p
        className={cn(
          'font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400',
          compact ? 'text-[8px]' : 'text-[10px]',
        )}
      >
        {label}
      </p>
      <p className={cn('font-bold text-slate-800 dark:text-slate-200', compact ? 'text-base' : 'text-lg')}>{value}</p>
      {detail && (
        <p className={cn('text-slate-500 dark:text-slate-400', compact ? 'text-[8px]' : 'text-[10px]')}>{detail}</p>
      )}
    </div>
  );
}

export function ConfidenceIndicator({ confidence, isApproved }: { confidence?: number | null; isApproved?: boolean }) {
  const { t } = useTranslation(); // <-- NEU
  const iconRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  if (isApproved || confidence == null || confidence >= GRADING_THRESHOLDS.MEDIUM_CONFIDENCE) return null;
  const isCritical = confidence < GRADING_THRESHOLDS.LOW_CONFIDENCE;
  const Icon = isCritical ? AlertOctagon : AlertTriangle;

  const showTooltip = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) setTooltipPos({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
  };

  return (
    <div
      ref={iconRef}
      className="relative inline-flex items-center ml-1.5 cursor-help"
      onMouseEnter={showTooltip}
      onMouseLeave={() => setTooltipPos(null)}
    >
      <Icon className={`w-4 h-4 ${isCritical ? 'text-red-500' : 'text-amber-500'}`} />
      {tooltipPos &&
        createPortal(
          <div
            className="fixed z-50 -translate-x-1/2 w-max bg-slate-800 text-white text-xs font-medium px-2 py-1 rounded shadow-lg"
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            {t('Canvas.summary.aiConfidenceHover', { percent: Math.round(confidence * 100) })}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-800"></div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Single-student rendering of the Schülerübersicht: same per-task values/logic as the teacher
 * table, but transposed to tasks-as-rows with one value column (there's only ever one student to
 * show), plus 3 trailing summary rows instead of trailing columns. */
function StudentOverviewSingleStudentTable({ store, s }: { store: SummaryStateStore; s: StudentSummary }) {
  const { t } = useTranslation();
  const { computed, actions } = store;
  return (
    <table className="min-w-full text-sm">
      <thead className="sticky top-0 z-10 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-secondary">
        <tr>
          <th className="px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
            {t('Canvas.summary.task')}
          </th>
          <th className="px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">{s.name}</th>
        </tr>
      </thead>
      <tbody>
        {computed.activeTaskDefinitions.map(tDef => (
          <Fragment key={tDef.taskKey}>
            <tr className="border-b border-slate-100 dark:border-slate-700">
              <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200">
                <span className="inline-flex items-center gap-1.5">
                  {tDef.label}
                  {computed.gradingStatusByTask[tDef.taskKey]?.isComplete && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  )}
                </span>
              </td>
              <td
                className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300 cursor-pointer hover:bg-slate-100/60 dark:hover:bg-secondary/60"
                title={s.gradingByTask[tDef.taskKey]}
                onClick={() => actions.onNavigateToTask?.(s.submissionIndex, tDef.taskKey)}
              >
                <div className="flex items-center">
                  <span>
                    {roundToOne(s.pointsByTask[tDef.taskKey] ?? 0)} / {roundToOne(tDef.maxPoints)}{' '}
                    {t('Canvas.summary.pointsAbbr')}
                  </span>
                  <ConfidenceIndicator
                    confidence={s.confidenceByTask[tDef.taskKey]}
                    isApproved={s.isApprovedByTask[tDef.taskKey]}
                  />
                </div>
              </td>
            </tr>
            {tDef.hasOwnLanguageUseCriteria && (
              <tr className="border-b border-slate-100 dark:border-slate-700">
                <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200">
                  {t('Canvas.summary.taskLanguageUseColumn', {
                    label: tDef.label,
                    defaultValue: `${tDef.label} · Sprachliche Leistung`,
                  })}
                </td>
                <td
                  className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300 cursor-pointer hover:bg-slate-100/60 dark:hover:bg-secondary/60"
                  onClick={() => actions.onNavigateToTask?.(s.submissionIndex, tDef.taskKey, 'grammar')}
                >
                  <div className="flex items-center">
                    <span>
                      {roundToOne(s.ownLanguageUsePointsByTask[tDef.taskKey] ?? 0)} /{' '}
                      {roundToOne(tDef.languageUseMaxPoints)} {t('Canvas.summary.pointsAbbr')}
                    </span>
                    <ConfidenceIndicator
                      confidence={s.ownLanguageUseConfidenceByTask[tDef.taskKey]}
                      isApproved={s.ownLanguageUseIsApprovedByTask[tDef.taskKey]}
                    />
                  </div>
                </td>
              </tr>
            )}
          </Fragment>
        ))}
        {computed.languageUseGroupColumns.map(col => (
          <tr key={col.schemeId} className="border-b border-slate-100 dark:border-slate-700">
            <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200">{col.label}</td>
            <td className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300">
              <div className="flex items-center">
                <span>
                  {roundToOne(s.languageUseGroupPointsBySchemeId[col.schemeId] ?? 0)} / {roundToOne(col.maxPoints)}
                </span>
                <ConfidenceIndicator
                  confidence={s.languageUseGroupConfidenceBySchemeId[col.schemeId]}
                  isApproved={s.languageUseGroupIsApprovedBySchemeId[col.schemeId]}
                />
              </div>
            </td>
          </tr>
        ))}
        {computed.grammarTaskDefinitions.length > 0 && (
          <tr className="border-b border-slate-100 dark:border-slate-700">
            <td
              className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200"
              title={t('Canvas.summary.grammarFiColumnHint')}
            >
              {t('Canvas.summary.grammar', { defaultValue: 'Grammatik' })}
            </td>
            <td
              className={cn(
                'whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300',
                s.firstGrammarTaskKey && 'cursor-pointer hover:bg-slate-100/60 dark:hover:bg-secondary/60',
              )}
              onClick={() =>
                s.firstGrammarTaskKey && actions.onNavigateToTask?.(s.submissionIndex, s.firstGrammarTaskKey, 'grammar')
              }
            >
              {s.grammarErrorCount === null ? (
                <span className="text-slate-400">–</span>
              ) : (
                <span title={t('Canvas.summary.fehlerindex', { defaultValue: 'Fehlerindex' })}>
                  <span
                    className={
                      s.grammarErrorCount > 0 ? 'font-semibold text-red-600' : 'text-slate-600 dark:text-slate-300'
                    }
                  >
                    {s.grammarErrorCount}
                  </span>
                  {s.grammarFehlerindex !== null && (
                    <span className="ml-1 text-xs text-slate-400">({s.grammarFehlerindex.toFixed(1)})</span>
                  )}
                </span>
              )}
            </td>
          </tr>
        )}
        <tr className="border-t-2 border-slate-200 dark:border-slate-600">
          <td className="whitespace-nowrap px-4 py-2 font-semibold text-slate-800 dark:text-slate-200">
            {t('Canvas.summary.total')}
          </td>
          <td className="whitespace-nowrap px-4 py-2 font-semibold text-slate-800 dark:text-slate-200">
            {roundToOne(s.totalPoints)} / {roundToOne(s.totalMaxPoints)}
          </td>
        </tr>
        <tr>
          <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200">
            {t('Canvas.summary.percentage')}
          </td>
          <td className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300">
            {roundToOne(s.percentage)}%
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export function StudentsOverviewTab({ store }: { store: SummaryStateStore }) {
  const { t } = useTranslation(); // <-- NEU
  const { state, computed, actions } = store;
  const isStudent = computed.role === 'student';
  const ownSummary = isStudent ? computed.filteredStudentSummaries[0] : undefined;

  if (isStudent) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card shadow-sm">
        <div className="border-b border-slate-100 dark:border-slate-700 px-4 py-3">
          <h3 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('Canvas.summary.studentsOverview')}
          </h3>
          <p className="break-words text-xs text-slate-500 dark:text-slate-400">
            {t('Canvas.summary.studentsOverviewDesc')}
          </p>
        </div>
        <div className="max-h-[60vh] overflow-auto">
          {ownSummary ? (
            <StudentOverviewSingleStudentTable store={store} s={ownSummary} />
          ) : (
            <p className="px-4 py-10 text-center text-sm text-slate-500">{t('Canvas.summary.noHits')}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-100 dark:border-slate-700 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('Canvas.summary.studentsOverview')}
          </h3>
          <p className="break-words text-xs text-slate-500 dark:text-slate-400">
            {t('Canvas.summary.studentsOverviewDesc')}
          </p>
        </div>
        <label className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input
            value={state.studentQuery}
            onChange={e => actions.setStudentQuery(e.target.value)}
            placeholder={t('Canvas.summary.searchStudent')}
            className="h-10 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-secondary pl-9 pr-3 text-sm text-slate-700 dark:text-slate-300 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25"
          />
        </label>
      </div>
      <div className="max-h-[60vh] overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-secondary">
            <tr>
              <th className="px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                {t('Canvas.summary.student')}
              </th>
              {computed.activeTaskDefinitions.map(tDef => (
                <Fragment key={tDef.taskKey}>
                  <th className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1.5">
                      {tDef.label}
                      {computed.gradingStatusByTask[tDef.taskKey]?.isComplete && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                      )}
                    </span>
                    <span className="ml-1 text-xs text-slate-400">
                      / {roundToOne(tDef.maxPoints)} {t('Canvas.summary.pointsAbbr')}
                    </span>
                  </th>
                  {tDef.hasOwnLanguageUseCriteria && (
                    <th className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                      {t('Canvas.summary.taskLanguageUseColumn', {
                        label: tDef.label,
                        defaultValue: `${tDef.label} · Sprachliche Leistung`,
                      })}
                      <span className="ml-1 text-xs text-slate-400">
                        / {roundToOne(tDef.languageUseMaxPoints)} {t('Canvas.summary.pointsAbbr')}
                      </span>
                    </th>
                  )}
                </Fragment>
              ))}
              {computed.languageUseGroupColumns.map(col => (
                <th
                  key={col.schemeId}
                  className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400"
                >
                  {col.label}
                  <span className="ml-1 text-xs text-slate-400">
                    / {roundToOne(col.maxPoints)} {t('Canvas.summary.pointsAbbr')}
                  </span>
                </th>
              ))}
              {computed.grammarTaskDefinitions.length > 0 && (
                <th
                  className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400"
                  title={t('Canvas.summary.grammarFiColumnHint')}
                >
                  {t('Canvas.summary.grammar', { defaultValue: 'Grammatik' })}
                </th>
              )}
              <th className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600">
                {t('Canvas.summary.total')}
              </th>
              <th className="px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                {t('Canvas.summary.percentage')}
              </th>
              <th className="px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                {t('Canvas.summary.grade')}
              </th>
            </tr>
          </thead>
          <tbody>
            {computed.filteredStudentSummaries.map(s => (
              <tr
                key={s.id}
                className="border-b border-slate-100 dark:border-slate-700 last:border-b-0 hover:bg-slate-50 dark:hover:bg-secondary/50 transition-colors"
              >
                <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200">{s.name}</td>
                {computed.activeTaskDefinitions.map(tDef => (
                  <Fragment key={tDef.taskKey}>
                    <td
                      className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300 cursor-pointer hover:bg-slate-100/60 dark:hover:bg-secondary/60"
                      title={s.gradingByTask[tDef.taskKey]}
                      onClick={() => actions.onNavigateToTask?.(s.submissionIndex, tDef.taskKey)}
                    >
                      <div className="flex items-center">
                        <span>
                          {roundToOne(s.pointsByTask[tDef.taskKey] ?? 0)} / {roundToOne(tDef.maxPoints)}
                        </span>
                        <ConfidenceIndicator
                          confidence={s.confidenceByTask[tDef.taskKey]}
                          isApproved={s.isApprovedByTask[tDef.taskKey]}
                        />
                      </div>
                    </td>
                    {tDef.hasOwnLanguageUseCriteria && (
                      <td
                        className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300 cursor-pointer hover:bg-slate-100/60 dark:hover:bg-secondary/60"
                        onClick={() => actions.onNavigateToTask?.(s.submissionIndex, tDef.taskKey, 'grammar')}
                      >
                        <div className="flex items-center">
                          <span>
                            {roundToOne(s.ownLanguageUsePointsByTask[tDef.taskKey] ?? 0)} /{' '}
                            {roundToOne(tDef.languageUseMaxPoints)}
                          </span>
                          <ConfidenceIndicator
                            confidence={s.ownLanguageUseConfidenceByTask[tDef.taskKey]}
                            isApproved={s.ownLanguageUseIsApprovedByTask[tDef.taskKey]}
                          />
                        </div>
                      </td>
                    )}
                  </Fragment>
                ))}
                {computed.languageUseGroupColumns.map(col => (
                  <td
                    key={`${s.id}-${col.schemeId}`}
                    className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300"
                  >
                    <div className="flex items-center">
                      <span>
                        {roundToOne(s.languageUseGroupPointsBySchemeId[col.schemeId] ?? 0)} /{' '}
                        {roundToOne(col.maxPoints)}
                      </span>
                      <ConfidenceIndicator
                        confidence={s.languageUseGroupConfidenceBySchemeId[col.schemeId]}
                        isApproved={s.languageUseGroupIsApprovedBySchemeId[col.schemeId]}
                      />
                    </div>
                  </td>
                ))}
                {computed.grammarTaskDefinitions.length > 0 && (
                  <td
                    className={cn(
                      'whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300',
                      s.firstGrammarTaskKey && 'cursor-pointer hover:bg-slate-100/60 dark:hover:bg-secondary/60',
                    )}
                    onClick={() =>
                      s.firstGrammarTaskKey &&
                      actions.onNavigateToTask?.(s.submissionIndex, s.firstGrammarTaskKey, 'grammar')
                    }
                  >
                    {s.grammarErrorCount === null ? (
                      <span className="text-slate-400">–</span>
                    ) : (
                      <span title={t('Canvas.summary.fehlerindex', { defaultValue: 'Fehlerindex' })}>
                        <span
                          className={
                            s.grammarErrorCount > 0
                              ? 'font-semibold text-red-600'
                              : 'text-slate-600 dark:text-slate-300'
                          }
                        >
                          {s.grammarErrorCount}
                        </span>
                        {s.grammarFehlerindex !== null && (
                          <span className="ml-1 text-xs text-slate-400">({s.grammarFehlerindex.toFixed(1)})</span>
                        )}
                      </span>
                    )}
                  </td>
                )}
                <td className="whitespace-nowrap px-4 py-2 font-semibold text-slate-800 dark:text-slate-200">
                  {roundToOne(s.totalPoints)} / {roundToOne(s.totalMaxPoints)}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300">
                  {roundToOne(s.percentage)}%
                </td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 font-semibold text-slate-700 dark:text-slate-300">
                    {s.grade}
                  </span>
                </td>
              </tr>
            ))}
            {computed.filteredStudentSummaries.length === 0 && (
              <tr>
                <td
                  colSpan={
                    computed.activeTaskDefinitions.length +
                    computed.activeTaskDefinitions.filter(tDef => tDef.hasOwnLanguageUseCriteria).length +
                    computed.languageUseGroupColumns.length +
                    (computed.grammarTaskDefinitions.length > 0 ? 1 : 0) +
                    4
                  }
                  className="px-4 py-10 text-center text-sm text-slate-500"
                >
                  {t('Canvas.summary.noHits')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ClarificationTab({ store }: { store: SummaryStateStore }) {
  const { t } = useTranslation(); // <-- NEU
  const { state, computed, actions } = store;
  const isStudent = computed.role === 'student';
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          {t('Canvas.summary.aiUncertainties')}
        </h3>
        <p className="mt-1 break-words text-sm text-slate-500">{t('Canvas.summary.aiUncertaintiesDesc')}</p>
        {/* flex-wrap (not a `md:` viewport breakpoint) so the sort controls drop to their own line
            exactly when they stop fitting next to the badge — driven by this box's own width, not
            the browser viewport, and with no fixed pixel threshold anywhere. */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
            {t('Canvas.summary.openDecisions', { count: computed.unresolvedCount })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">{t('Canvas.summary.sortBy')}</span>
            {/* flex-wrap here too: this pill was rigid (inline-flex, no wrap), so even once the outer
                row put it on its own line, the 3 buttons alone could still be wider than the box and
                overflow. Letting them wrap is what actually stops that, still with no fixed widths.
                The direction button lives inside this same pill (last item) so it wraps together with
                the sort-field buttons as one group instead of potentially landing on its own line. */}
            <div className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-secondary p-1">
              {!isStudent && (
                <button
                  onClick={() => actions.setSortField('student')}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                    state.sortField === 'student'
                      ? 'bg-white dark:bg-card text-slate-800 dark:text-slate-200 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400',
                  )}
                >
                  {t('Canvas.summary.student')}
                </button>
              )}
              <button
                onClick={() => actions.setSortField('task')}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                  state.sortField === 'task'
                    ? 'bg-white dark:bg-card text-slate-800 dark:text-slate-200 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400',
                )}
              >
                {t('Canvas.summary.task')}
              </button>
              <button
                onClick={() => actions.setSortField('uncertainty')}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                  state.sortField === 'uncertainty'
                    ? 'bg-white dark:bg-card text-slate-800 dark:text-slate-200 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400',
                )}
              >
                {t('Canvas.summary.aiUncertainty')}
              </button>
              <button
                onClick={() => actions.setSortDirection(p => (p === 'asc' ? 'desc' : 'asc'))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-card text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-secondary"
              >
                {state.sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {computed.sortedClarificationCases.map(item => {
          const isConfirmed = state.clarificationDecisions[item.id] === 'approve';
          const isSaving = state.clarificationSaving?.[item.id] === true;
          return (
            <div
              key={item.id}
              className={cn(
                'rounded-xl border bg-white dark:bg-card p-4 shadow-sm transition-colors',
                isConfirmed
                  ? 'border-green-200 dark:border-green-800 bg-green-50/40 dark:bg-green-900/10'
                  : 'border-slate-200 dark:border-slate-700',
              )}
            >
              {/* Always column (no `lg:` viewport breakpoint) — the buttons sit below the explanation
                  text rather than squeezed into a side column, which is also what was really forcing
                  the overshoot the min-w-0/truncate below now only has to guard defensively. */}
              <div className="flex flex-col gap-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {item.studentName} - {item.taskLabel}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t('Canvas.summary.confidence')}{' '}
                    <span className="font-semibold text-amber-700">{Math.round(item.confidence * 100)}%</span> |{' '}
                    {t('Canvas.summary.points')} {roundToOne(item.points)} / {roundToOne(item.maxPoints)}
                  </p>
                  <p className="break-words text-sm text-slate-600 dark:text-slate-400">{item.reason}</p>
                </div>
                {/* min-w-0 the whole way down (column -> button -> label span) instead of the old
                    min-w-32 floor, which is exactly what forced these buttons wider than the box at
                    a narrow width. The label now truncates instead of forcing an overshoot. */}
                <div className="flex min-w-0 flex-col gap-2">
                  <button
                    onClick={() => actions.handleNavigateToClarificationTask(item)}
                    className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-secondary px-3 py-1.5 text-sm font-semibold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <span className="min-w-0 truncate">{t('Canvas.summary.toGrading')}</span>
                  </button>
                  <button
                    onClick={() => void actions.handleClarificationDecision(item, 'approve')}
                    disabled={isConfirmed || isSaving}
                    className={cn(
                      'inline-flex min-w-0 items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
                      isConfirmed
                        ? 'cursor-default border-green-300 bg-green-100 text-green-700'
                        : 'border-brand-500 bg-brand-500 text-white hover:bg-[brand-600]',
                    )}
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 truncate">
                      {isConfirmed ? t('Canvas.summary.confirmed') : t('Canvas.summary.confirm')}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {computed.sortedClarificationCases.length === 0 && (
          <div className="rounded-xl border border-border bg-white dark:bg-card p-8 text-center text-sm text-slate-500 dark:text-slate-400 shadow-sm">
            {t('Canvas.summary.noClarificationNeeded')}
          </div>
        )}
      </div>
    </div>
  );
}

export function TaskInsightsTab({ store }: { store: SummaryStateStore }) {
  const { t } = useTranslation(); // <-- NEU
  const { computed } = store;
  // Own-language-use rows share the task's plain label with the content row, so they need the same
  // "· Sprachliche Leistung" suffix StudentsOverviewTab uses for that column; group labels are already final.
  const formatTaskAverageLabel = (row: { kind: 'content' | 'ownLanguageUse' | 'group'; label: string }): string =>
    row.kind === 'ownLanguageUse'
      ? t('Canvas.summary.taskLanguageUseColumn', {
          label: row.label,
          defaultValue: `${row.label} · Sprachliche Leistung`,
        })
      : row.label;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
          <p className="break-words text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t('Canvas.summary.worstAvg')}
          </p>
          <p className="mt-2 break-words text-lg font-bold text-slate-800">
            {computed.worstTask ? formatTaskAverageLabel(computed.worstTask) : t('Canvas.summary.noDataYet')}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {computed.worstTask
              ? `${roundToOne(computed.worstTask.avgPercentage)}% ${t('Canvas.summary.avgSuffix')}`
              : ''}
          </p>
        </div>
        <div className="min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
          <p className="break-words text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t('Canvas.summary.frequentErrors')}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {computed.frequentErrorSources.map(s => (
              <span
                key={s.label}
                className="inline-block min-w-0 max-w-full break-all rounded-lg border bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700"
              >
                {s.label} ({s.count})
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          {t('Canvas.summary.metricsPerTask')}
        </h3>
        <div className="mt-3 space-y-2">
          {computed.taskAverages.map(tDef => (
            <div key={tDef.key} className="flex items-center gap-3">
              <span className="w-32 min-w-0 break-words text-sm font-semibold text-slate-700">
                {formatTaskAverageLabel(tDef)}
              </span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-brand-500" style={{ width: `${tDef.avgPercentage}%` }} />
              </div>
              <span className="w-36 text-right text-xs text-slate-600">
                {roundToOne(tDef.avgPoints)} / {roundToOne(tDef.maxPoints)} ({roundToOne(tDef.avgPercentage)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function GradeDistributionTab({ store }: { store: SummaryStateStore }) {
  const { t } = useTranslation(); // <-- NEU
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-700">{t('Canvas.summary.gradeDistribution')}</h3>
      <table className="min-w-full text-sm mt-4 overflow-hidden rounded-lg border border-slate-200 block">
        <thead className="bg-slate-50 w-full table table-fixed">
          <tr className="table-row">
            <th className="px-4 py-2 text-left">{t('Canvas.summary.grade')}</th>
            <th className="px-4 py-2 text-left">{t('Canvas.summary.count')}</th>
          </tr>
        </thead>
        <tbody className="w-full table table-fixed">
          {store.computed.gradeDistribution.map(i => (
            <tr key={i.grade} className="border-t">
              <td className="px-4 py-2 font-medium">{i.grade}</td>
              <td className="px-4 py-2">{i.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CurvingTab({ store }: { store: SummaryStateStore }) {
  const { t } = useTranslation();
  const { state, actions } = store;
  const scale = state.gradingScale;

  const scaleLabels: Record<string, string> = {
    'german-1-6': t('Canvas.summary.gradingScales.german16'),
    drittel: t('Canvas.summary.gradingScales.drittel'),
    'a-f': t('Canvas.summary.gradingScales.af'),
    'gpa-4': t('Canvas.summary.gradingScales.gpa4'),
    notenpunkte: t('Canvas.summary.gradingScales.notenpunkte'),
    'numeric-10': t('Canvas.summary.gradingScales.numeric10'),
    percentage: t('Canvas.summary.gradingScales.percentage'),
  };

  const handleBorderChange = (index: number, value: number) => {
    // Last border is always 0% and not editable
    if (index === scale.borders.length - 1) return;
    const updatedBorders = updateBorderMinPercentage(scale.borders, index, value);
    actions.updateGradingScale({ ...scale, type: 'custom', borders: updatedBorders });
  };

  const handleSelectPredefined = (key: string) => {
    const predefined = PREDEFINED_GRADING_SCALES[key];
    if (predefined) actions.updateGradingScale(predefined);
  };

  return (
    <div className="space-y-4">
      {/* Scale selector */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t('Canvas.summary.gradeSystem')}</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(scaleLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => handleSelectPredefined(key)}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
                scale.type === key
                  ? 'border-brand-500 bg-brand-500/10 text-[var(--color-brand-700)]'
                  : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-secondary text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700',
              )}
            >
              {label}
            </button>
          ))}
          {scale.type === 'custom' && (
            <span className="rounded-lg border border-brand-500 bg-brand-500/10 px-3 py-1.5 text-sm font-semibold text-[var(--color-brand-700)]">
              {t('Canvas.summary.gradingScales.custom')}
            </span>
          )}
        </div>
      </div>

      {/* Border editor */}
      {scale.type !== 'percentage' && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('Canvas.summary.gradeBorders')}
          </h3>
          <div className="mt-4 space-y-3">
            {scale.borders.map((border, idx) => {
              const isLowest = idx === scale.borders.length - 1;
              return (
                <div key={border.label} className="grid grid-cols-[80px_1fr_70px] items-center gap-3">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                    {border.label}
                  </span>
                  {isLowest ? (
                    <span className="text-xs text-slate-400 dark:text-slate-500 col-span-2">
                      {t('Canvas.summary.lowestGradeLabel')}
                    </span>
                  ) : (
                    <>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={border.minPercentage}
                        onChange={e => handleBorderChange(idx, Number(e.target.value))}
                        className="h-2 w-full cursor-pointer bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none"
                      />
                      <span className="text-right text-sm font-semibold text-slate-700 dark:text-slate-300">
                        {border.minPercentage}%
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Curving / Normal distribution */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="min-w-0">
            <h3 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
              {t('Canvas.summary.normalDistribution')}
            </h3>
          </div>
          <button
            onClick={() => actions.setIsCurveEnabled(!state.isCurveEnabled)}
            disabled={true}
            title={t('Canvas.summary.curvingComingSoon')}
            className={cn(
              'min-w-0 max-w-full break-words rounded-lg border px-3 py-1.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50',
              state.isCurveEnabled
                ? 'border-brand-500 bg-brand-500/10 text-[var(--color-brand-700)]'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-secondary text-slate-600 dark:text-slate-400',
            )}
          >
            {state.isCurveEnabled ? t('Canvas.summary.activated') : t('Canvas.summary.deactivated')}
          </button>
        </div>
        <div className="mt-4 grid grid-cols-[minmax(0,140px)_minmax(0,1fr)_minmax(0,70px)] items-center gap-3">
          <span className="min-w-0 break-words text-sm text-slate-700 dark:text-slate-300">
            {t('Canvas.summary.curveStrength')}
          </span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(state.curveStrength * 100)}
            onChange={e => actions.setCurveStrength(Number(e.target.value) / 100)}
            disabled={true} //{!state.isCurveEnabled}
            title={t('Canvas.summary.curvingComingSoon')}
            className="h-2 w-full cursor-pointer bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="min-w-0 break-words text-right text-sm font-semibold text-slate-700 dark:text-slate-300">
            {Math.round(state.curveStrength * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
}

/** Single-student rendering of the Grammatik matrix: same per-task grammar values/logic as the
 * teacher matrix, but transposed to tasks-as-rows with one value column, plus 4 trailing summary
 * rows (Gesamt/Fehlerindex/Note/Note mit FI) instead of trailing columns. */
function GrammarMatrixSingleStudentTable({ store, s }: { store: SummaryStateStore; s: StudentSummary }) {
  const { t } = useTranslation();
  const { computed, actions } = store;
  const totalErrors = computed.activeTaskDefinitions.reduce(
    (sum, tDef) =>
      computed.grammarEligibleTaskKeys.has(tDef.taskKey) ? sum + (s.grammarByTask[tDef.taskKey]?.errorCount ?? 0) : sum,
    0,
  );
  return (
    <table className="min-w-full text-sm">
      <thead className="sticky top-0 z-10 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-secondary">
        <tr>
          <th className="px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
            {t('Canvas.summary.task')}
          </th>
          <th className="px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">{s.name}</th>
        </tr>
      </thead>
      <tbody>
        {computed.activeTaskDefinitions.map(tDef => {
          const isEligible = computed.grammarEligibleTaskKeys.has(tDef.taskKey);
          const grammar = s.grammarByTask[tDef.taskKey];
          return (
            <tr key={tDef.taskKey} className="border-b border-slate-100 dark:border-slate-700">
              <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200">
                {tDef.label}
              </td>
              <td
                className={cn(
                  'whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300',
                  isEligible && grammar && 'cursor-pointer hover:bg-slate-100/60 dark:hover:bg-secondary/60',
                )}
                onClick={() =>
                  isEligible && grammar && actions.onNavigateToTask?.(s.submissionIndex, tDef.taskKey, 'grammar')
                }
              >
                {isEligible && grammar ? (
                  <span>
                    <span
                      className={
                        grammar.errorCount > 0 ? 'font-semibold text-red-600' : 'text-slate-600 dark:text-slate-300'
                      }
                    >
                      {grammar.errorCount}
                    </span>
                    <span className="ml-1 text-xs text-slate-400">({grammar.fehlerindex.toFixed(1)})</span>
                  </span>
                ) : (
                  <span className="text-slate-400">–</span>
                )}
              </td>
            </tr>
          );
        })}
        <tr className="border-t-2 border-slate-200 dark:border-slate-600">
          <td className="whitespace-nowrap px-4 py-2 font-semibold text-slate-800 dark:text-slate-200">
            {t('Canvas.summary.total')}
          </td>
          <td className="whitespace-nowrap px-4 py-2 font-semibold text-slate-800 dark:text-slate-200">
            {totalErrors}
          </td>
        </tr>
        <tr>
          <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200">
            {t('Canvas.summary.fehlerindex', { defaultValue: 'Fehlerindex' })}
          </td>
          <td className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300">
            {s.grammarFehlerindex !== null ? s.grammarFehlerindex.toFixed(1) : '–'}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export function LanguageUseTab({ store }: { store: SummaryStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions } = store;
  const settings = state.grammarFiSettings;
  const isFiMode = computed.resolvedAlwaysUseFi;
  const isStudent = computed.role === 'student';
  const ownSummary = isStudent ? computed.filteredStudentSummaries[0] : undefined;
  // Grade-step deductions have no meaning on a percentage scale (no discrete borders to shift).
  const fiSupportedByScale = state.gradingScale.type !== 'percentage';
  const [isBordersExpanded, setIsBordersExpanded] = useState(false);

  const updateThreshold = (index: number, patch: Partial<GrammarFiThreshold>) => {
    const thresholds = settings.thresholds.map((threshold, i) =>
      i === index ? { ...threshold, ...patch } : { ...threshold },
    );
    actions.updateGrammarFiSettings({ ...settings, thresholds });
  };

  if (!computed.hasAnyGrammarEligibleTask) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-8 text-center text-sm text-slate-500 dark:text-slate-400 shadow-sm">
        {t('Canvas.summary.grammarTab.noGrammarTasks')}
      </div>
    );
  }

  // Binary switch: no visible "inherit" state — it always displays the effective value (exam
  // override if explicitly set, else the teacher's global default) and every toggle writes an
  // explicit per-exam boolean, same mutation as before.
  const setAlwaysUseFi = (value: boolean) => actions.updateGrammarFiSettings({ ...settings, alwaysUseFi: value });

  const hasAnyRules = computed.ownLanguageUseRuleEntries.length > 0 || computed.languageUseGroupColumns.length > 0;

  return (
    <div className="space-y-4">
      {/* Per-exam FI switch. Pre-set to the effective value (exam override, else teacher default);
          toggling always writes an explicit per-exam override. */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
              {t('Canvas.summary.grammarTab.fiSwitchTitle')}
            </h3>
            <p className="mt-1 break-words text-xs text-slate-500 dark:text-slate-400">
              {t('Canvas.summary.grammarTab.fiSwitchDesc')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAlwaysUseFi(!computed.resolvedAlwaysUseFi)}
            className={cn(
              'w-11 h-6 rounded-full transition-colors relative shrink-0',
              computed.resolvedAlwaysUseFi ? 'bg-brand-500' : 'bg-slate-200 dark:bg-slate-700',
            )}
          >
            <span
              className={cn(
                'absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform shadow-sm',
                computed.resolvedAlwaysUseFi ? 'translate-x-5' : 'translate-x-0',
              )}
            />
          </button>
        </div>

        {/* Grade-step deduction borders, only relevant once FI actually applies. Collapsible since
            it's rarely touched once configured. */}
        {isFiMode && (
          <div className="mt-4 border-t border-slate-100 dark:border-slate-700 pt-4">
            <button
              type="button"
              onClick={() => setIsBordersExpanded(prev => !prev)}
              className="flex w-full items-center gap-1.5 text-left"
            >
              {isBordersExpanded ? (
                <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
              )}
              <h4 className="min-w-0 break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
                {t('Canvas.summary.grammarTab.fiTitle')}
              </h4>
            </button>
            <p className="mt-1 break-words text-xs text-slate-500 dark:text-slate-400">
              {fiSupportedByScale
                ? t('Canvas.summary.grammarTab.fiDesc')
                : t('Canvas.summary.grammarTab.fiUnsupportedScale')}
            </p>
            {isBordersExpanded && (
              <div className="mt-4 max-w-xl divide-y divide-slate-100 dark:divide-slate-700">
                {/* Never offer more than two borders. */}
                {settings.thresholds.slice(0, 2).map((threshold, idx) => (
                  <div key={idx} className="flex flex-wrap gap-4 py-2 first:pt-0 last:pb-0">
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="min-w-0 break-words text-sm text-slate-700 dark:text-slate-300">
                        {t('Canvas.summary.grammarTab.fiAbove')}
                      </span>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={threshold.minFi}
                        disabled={!fiSupportedByScale}
                        onChange={e => updateThreshold(idx, { minFi: Number(e.target.value) })}
                        className="h-9 w-20 min-w-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-secondary px-2 text-sm text-slate-700 dark:text-slate-300 outline-none focus:border-brand-500 disabled:opacity-50"
                      />
                    </div>
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="min-w-0 break-words text-sm text-slate-700 dark:text-slate-300">
                        {t('Canvas.summary.grammarTab.deductSteps')}
                      </span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={threshold.steps}
                        disabled={!fiSupportedByScale}
                        onChange={e => updateThreshold(idx, { steps: Math.max(1, Math.round(Number(e.target.value))) })}
                        className="h-9 w-20 min-w-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-secondary px-2 text-sm text-slate-700 dark:text-slate-300 outline-none focus:border-brand-500 disabled:opacity-50"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Student × task grammar-error matrix — like the Schülerübersicht, every task gets a column;
          tasks that aren't grammar-eligible at all show "–" instead of a count. */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card shadow-sm">
        <div className="border-b border-slate-100 dark:border-slate-700 px-4 py-3">
          <h3 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('Canvas.summary.grammarTab.matrixTitle')}
          </h3>
          <p className="break-words text-xs text-slate-500 dark:text-slate-400">
            {t('Canvas.summary.grammarTab.matrixDesc')}
          </p>
        </div>
        <div className="max-h-[60vh] overflow-auto">
          {isStudent ? (
            ownSummary ? (
              <GrammarMatrixSingleStudentTable store={store} s={ownSummary} />
            ) : (
              <p className="px-4 py-10 text-center text-sm text-slate-500">{t('Canvas.summary.noHits')}</p>
            )
          ) : (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-secondary">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                    {t('Canvas.summary.student')}
                  </th>
                  {computed.activeTaskDefinitions.map(tDef => (
                    <th
                      key={tDef.taskKey}
                      className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400"
                    >
                      {tDef.label}
                    </th>
                  ))}
                  <th className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                    {t('Canvas.summary.total')}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                    {t('Canvas.summary.fehlerindex', { defaultValue: 'Fehlerindex' })}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                    {t('Canvas.summary.grade')}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-600 dark:text-slate-400">
                    {t('Canvas.summary.grammarTab.gradeWithFi')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {computed.filteredStudentSummaries.map(s => {
                  const totalErrors = computed.activeTaskDefinitions.reduce(
                    (sum, tDef) =>
                      computed.grammarEligibleTaskKeys.has(tDef.taskKey)
                        ? sum + (s.grammarByTask[tDef.taskKey]?.errorCount ?? 0)
                        : sum,
                    0,
                  );
                  return (
                    <tr
                      key={s.id}
                      className="border-b border-slate-100 dark:border-slate-700 last:border-b-0 hover:bg-slate-50 dark:hover:bg-secondary/50 transition-colors"
                    >
                      <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800 dark:text-slate-200">
                        {s.name}
                      </td>
                      {computed.activeTaskDefinitions.map(tDef => {
                        const isEligible = computed.grammarEligibleTaskKeys.has(tDef.taskKey);
                        const grammar = s.grammarByTask[tDef.taskKey];
                        return (
                          <td
                            key={`${s.id}-${tDef.taskKey}`}
                            className={cn(
                              'whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300',
                              isEligible &&
                                grammar &&
                                'cursor-pointer hover:bg-slate-100/60 dark:hover:bg-secondary/60',
                            )}
                            onClick={() =>
                              isEligible &&
                              grammar &&
                              actions.onNavigateToTask?.(s.submissionIndex, tDef.taskKey, 'grammar')
                            }
                          >
                            {isEligible && grammar ? (
                              <span>
                                <span
                                  className={
                                    grammar.errorCount > 0
                                      ? 'font-semibold text-red-600'
                                      : 'text-slate-600 dark:text-slate-300'
                                  }
                                >
                                  {grammar.errorCount}
                                </span>
                                <span className="ml-1 text-xs text-slate-400">({grammar.fehlerindex.toFixed(1)})</span>
                              </span>
                            ) : (
                              <span className="text-slate-400">–</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="whitespace-nowrap px-4 py-2 font-semibold text-slate-800 dark:text-slate-200">
                        {totalErrors}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-slate-700 dark:text-slate-300">
                        {s.grammarFehlerindex !== null ? s.grammarFehlerindex.toFixed(1) : '–'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2">
                        <span className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 font-semibold text-slate-700 dark:text-slate-300">
                          {s.baseGrade}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2">
                        {s.gradeWithFiApplied !== s.baseGrade ? (
                          <span className="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-2 py-0.5 font-semibold text-red-700">
                            {s.gradeWithFiApplied}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 font-semibold text-slate-700 dark:text-slate-300">
                            {s.gradeWithFiApplied}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Which tasks have local (own) or global (group) language-use rules and were used for grading. */}
        {hasAnyRules && (
          <div className="border-t border-slate-100 dark:border-slate-700 px-4 py-3">
            <h4 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
              {t('Canvas.summary.languageUseTab.rulesOverviewTitle')}
            </h4>
            <ul className="mt-2 space-y-1 break-words text-sm text-slate-600 dark:text-slate-400">
              {computed.ownLanguageUseRuleEntries.map(entry => (
                <li key={entry.taskKey}>
                  {t('Canvas.summary.languageUseTab.ownRuleEntry', {
                    label: entry.label,
                    points: roundToOne(entry.maxPoints),
                  })}
                </li>
              ))}
              {computed.languageUseGroupColumns.map(col => (
                <li key={col.schemeId}>
                  {t('Canvas.summary.languageUseTab.groupRuleEntry', {
                    label: col.label,
                    points: roundToOne(col.maxPoints),
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export function ExportTab({ store }: { store: SummaryStateStore }) {
  const { t } = useTranslation(); // <-- NEU
  const { computed, props, actions } = store;
  return (
    <div className="grid grid-cols-1 gap-4">
      <DistributeResultsCard examId={props.exam?._id} />
      <div className="min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <h3 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
          {t('Canvas.summary.pdfReports')}
        </h3>
        <button
          disabled={computed.isPdfDownloadDisabled}
          onClick={() => actions.onDownloadReports?.()}
          title={!props.canDownloadReports ? (props.downloadDisabledReason ?? t('Canvas.summary.notMet')) : undefined}
          className="mt-4 inline-flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-border bg-white dark:bg-secondary px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          <Download className="h-4 w-4 shrink-0" />
          <span className="min-w-0 break-words">
            {props.isDownloadingReports ? t('Canvas.summary.downloadingPdf') : t('Canvas.summary.downloadPdf')}
          </span>
        </button>
      </div>
      <div className="min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <h3 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
          {t('Canvas.summary.csvExport')}
        </h3>
        <button
          disabled={computed.isCsvDownloadDisabled}
          onClick={() => actions.onDownloadGradebookCsv?.()}
          title={
            !actions.onDownloadGradebookCsv
              ? t('Canvas.summary.notConnected')
              : !props.canDownloadGradebookCsv
                ? (props.gradebookCsvDisabledReason ?? t('Canvas.summary.notMet'))
                : undefined
          }
          className="mt-4 inline-flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-border bg-white dark:bg-secondary px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSpreadsheet className="h-4 w-4 shrink-0" />
          <span className="min-w-0 break-words">
            {props.isDownloadingGradebookCsv ? t('Canvas.summary.downloadingCsv') : t('Canvas.summary.downloadCsv')}
          </span>
        </button>
      </div>
    </div>
  );
}

function updateBorderMinPercentage(
  borders: GradingScaleBorder[],
  index: number,
  rawValue: number,
): GradingScaleBorder[] {
  const next = borders.map(b => ({ ...b }));
  const clamped = Math.max(0, Math.min(100, Math.round(rawValue)));
  // Enforce descending order: must be below the border above and above the border below
  const above = index > 0 ? (next[index - 1]?.minPercentage ?? 100) : 101;
  const below = index < next.length - 2 ? (next[index + 1]?.minPercentage ?? 0) : -1;
  const b = next[index];
  if (b) b.minPercentage = Math.max(below + 1, Math.min(above - 1, clamped));
  return next;
}
